import React, { useEffect, useRef } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import Select, { SelectInstance, StylesConfig } from 'react-select';

import flagSprite from '@lt/components/components/Flags';
import { DefaultCountriesOption, DestinationOption } from '@lt/api/dist/methods/references/types';

import { isIOS, isTouchScreen } from '../../../../utils';
import { SEARCH_TYPE_NAMES } from '../../constants';
import { useEventListener } from 'usehooks-ts';
import { ArrowLeftIcon } from '../BackWizardButton/BackWizardButton';
import {
  mobileDefaultControlStyles,
  mobileHotelPageControlStyles,
  MobileLabel,
  mobileWidgetInputStyles,
} from '../../styles';
import { DestinationPickerProps } from './types';
import Control from './components/Control';
import Group from './components/Group';
import Option from './components/Option';
import Input from './components/Input';
import { NoOptionsMessage } from './components/NoOptionsMessage/NoOptionsMessage';
import SingleValue from './components/SingleValue';
import ValueContainer from './components/ValueContainer';
import { placeholder, destinationPickerName, DEFAULT_IMAGE } from './constants';
import CloseButton from '../CloseButton';

const FlagSprites = createGlobalStyle`
  ${flagSprite()}
`;

export const DestinationPicker = ({
  options,
  isMobile = false,
  value,
  inputValue,
  onInputChange,
  isWizardOpen,
  isWizard,
  menuIsOpen,
  isDisabled,
  onMenuOpen,
  onMenuClose,
  onChange,
  changeOptions,
  className,
  searchType,
  variant = 'default',
  isWidget = false,
  isLoading,
}: DestinationPickerProps): JSX.Element => {
  const ref = useRef<SelectInstance<DestinationOption>>(null);
  const showBackButton = !!menuIsOpen && isWizard;
  const showCloseButton = isMobile && !!menuIsOpen && !isWizard;
  const showLoader = isLoading && !inputValue && searchType !== SEARCH_TYPE_NAMES.TRIP;
  const { image = '' } = (value || {}) as DefaultCountriesOption;

  useEffect(() => {
    if (menuIsOpen && ref.current) {
      ref.current.focus();

      if (isIOS && !isWidget) {
        setTimeout(() => {
          // Хак из-за того, что из-за клавиатуры инпут на айфоне улетает вверх
          // TODO: Убрать это и сделать нормально
          window.scrollTo(0, 0);
        }, 500);
      }
    }
  }, [isWidget, menuIsOpen]);

  /**
   * После открытия меню в визарде скрываем клавиатуру
   * Без этого в некоторых версиях iOS нельзя отредактировать поле
   */
  useEffect(() => {
    if (!isWizard) return;

    const blurTimer = setTimeout(() => {
      ref.current?.inputRef?.blur();
    }, 200);

    return () => {
      clearTimeout(blurTimer);
    };
  }, [isWizard]);

  /**
   * При переключении типа поиска на экскурсии нужно очистить поле, если
   * до этого в нём была опция другого типа поиска
   * И наоборот при переключении типа поиска с экскурсий на обычный
   * нужно очистить поле, т.к. иначе поиск будет не того формата
   */
  useEffect(() => {
    /* TODO: LT-37919 - разрулить некорректные данные на уровне стейта */
    if (!isTouchScreen) return;

    ref.current?.clearValue();
  }, [searchType]);

  // Если тыкаем где-то за пределами инпута, то скрываем клавиатуру на мобильном устройстве
  useEventListener('touchstart', (event) => {
    const el = ref.current?.controlRef;

    if (!el || el.contains(event.target as Node)) {
      // Хак для Webview, без этого не работает тач по полю
      isWizard && onMenuOpen && onMenuOpen();
      return;
    }

    setTimeout(() => {
      ref.current?.inputRef?.blur();
    }, 200);
  });

  // По клику в инпут сдвигаем мигающий курсор в конец строки
  // и убираем выделение содержимого инпута
  const onFocus = () => {
    const inputRef = ref.current?.inputRef;
    if (!inputRef) return;

    value && inputRef.select();

    setTimeout(() => {
      inputRef.setSelectionRange(inputRef.value.length, inputRef.value.length);
    }, 0);
  };

  const isAbsolute = !isWidget || (isWidget && isMobile);
  const selectStyles: StylesConfig<DestinationOption> = {
    placeholder: (styles) => ({
      ...styles,
      ...(isAbsolute && { position: 'absolute' }),
    }),
    control: (styles) => ({
      ...styles,
      ...(showLoader && { justifyContent: 'flex-start' }),
    }),
    loadingIndicator: (styles) => ({
      ...styles,
      ...(showLoader && { position: 'absolute', top: '16px', left: '32px' }),
    }),
  };

  return (
    <>
      {isMobile && !isWizard && (
        <>
          <StyledMobileLabel $variant={variant}>{placeholder(true)}</StyledMobileLabel>
          {menuIsOpen && <StyledCloseButton onClick={onMenuClose} />}
        </>
      )}
      <SelectContainer
        $withBackButton={showBackButton}
        $withCloseButton={showCloseButton}
        $isMobile={isMobile}
        $isWizardOpen={isWizardOpen}
        $image={image}
        $isWizard={isWizard}
        $menuIsOpen={menuIsOpen}
        className={className}
        $searchType={searchType}
        $hasVisaLabel={Boolean((value as DefaultCountriesOption)?.visa)}
        $variant={variant}
        $isWidget={isWidget}
      >
        <FlagSprites />
        <Select<DestinationOption>
          styles={selectStyles}
          ref={ref}
          onInputChange={onInputChange}
          className={destinationPickerName}
          classNamePrefix={destinationPickerName}
          placeholder={showLoader ? '' : placeholder(menuIsOpen, searchType)}
          options={options}
          isClearable
          backspaceRemovesValue
          escapeClearsValue={false}
          inputValue={!isTouchScreen || (isTouchScreen && menuIsOpen) ? inputValue : undefined}
          /**
           * Нужно, чтобы при очищении поля в нём не оставалось
           * ранее выбранное значение
           */
          value={inputValue ? value : null}
          onMenuOpen={onMenuOpen}
          filterOption={() => true}
          onMenuClose={isTouchScreen ? undefined : onMenuClose}
          isMobile={isTouchScreen}
          onChange={onChange}
          onFocus={onFocus}
          menuIsOpen={menuIsOpen}
          menuShouldScrollIntoView={false}
          isDisabled={isDisabled}
          changeOptions={changeOptions}
          isMulti={false}
          components={{
            Control,
            Group,
            Option,
            Input,
            NoOptionsMessage,
            LoadingMessage: () => null,
            IndicatorSeparator: () => null,
            DropdownIndicator: () => null,
            ClearIndicator: () => null,
            ...(isTouchScreen ? { SingleValue } : { SingleValue: () => null }),
            ValueContainer,
          }}
          isSearchable={!isTouchScreen || menuIsOpen}
          isLoading={showLoader}
          aria-label={placeholder(menuIsOpen)}
        />
      </SelectContainer>
    </>
  );
};

const SelectContainer = styled.div<{
  $withBackButton?: boolean;
  $isMobile: boolean;
  $isWizard?: boolean;
  $isWizardOpen?: boolean;
  $image: string;
  $menuIsOpen?: boolean;
  $withCloseButton?: boolean;
  $searchType?: string;
  $hasVisaLabel?: boolean;
  $variant?: 'default' | 'hotel';
  $isWidget: boolean;
}>`
  display: flex;
  position: relative;
  align-items: center;
  width: 100%;
  flex-grow: ${({ $isMobile }) => ($isMobile ? 0 : 2)};
  ${({ $isMobile, $isWizard, $menuIsOpen }) =>
    $isMobile &&
    !$isWizard &&
    $menuIsOpen &&
    css`
      position: fixed;
      top: 0;

      &:before {
        content: '';
        top: 0;
        background: black;
      }
    `}

  .${destinationPickerName} {
    width: 100%;
    position: initial;
    background-color: #fff;
    border-radius: 4px 0 0 4px;
    color: #17181a;

    &__control {
      min-width: 200px;
      padding: 8px;
      font-size: 14px;
      line-height: 16px;
      border: 0;
      box-shadow: none;
      height: 56px;
      caret-color: #007aff;
      border-radius: 4px 0 0 4px;
      color: #17181a;
      font-weight: bold;

      &--menu-is-open {
        font-weight: normal;
      }

      &:hover {
        border: 0;
        box-shadow: none;
      }

      &-visa {
        display: none;
      }
    }
    &__option {
      position: relative;
      padding: 0;
      color: inherit;
      width: initial;

      &--is-focused {
        background-color: #e6f2ff;
      }

      &--is-selected {
        background-color: #e6f2ff;
      }
    }

    &__placeholder {
      color: #919499;
      font-weight: normal;
    }

    &__placeholder,
    &__input-container {
      padding: 0;
      margin: 0;
    }

    &__input-container {
      visibility: visible;
    }

    /* увеличиваем и скругляем лоадер react-select'а */
    &__indicators span {
      height: 10px;
      width: 10px;
      border-radius: 50%;
    }

    ${({ $isMobile }) =>
      !$isMobile &&
      `
      /* fix small input touch region bug */
      &__input-container {
        display: block;

        & > input {
          display: inline-block;
        }

        &::after {
          content: none;
        }
      }
    `}

    &__menu {
      margin: 0;
      box-shadow: 0 2px 4px rgb(210 208 197 / 80%);
      width: 320px;
      z-index: 9999;
    }

    &__menu-list {
      position: initial;
      max-height: 554px;
      padding: 0;
    }

    &__group-heading {
      text-transform: none;
      font-size: 12px;
      line-height: 16px;
      padding-left: 8px;
      padding-right: 8px;
    }

    &__countries,
    &__popularCountries,
    &__popularDirections {
      & .${destinationPickerName}__option::after, .${destinationPickerName}__option::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        background: linear-gradient(90deg, #ffffff 33.85%, rgba(255, 255, 255, 0) 100%);
      }
      &
        .${destinationPickerName}__option--is-selected::after,
        .${destinationPickerName}__option--is-focused::after {
        background: linear-gradient(90deg, #e6f2ff 33.85%, rgba(230, 242, 255, 0) 100%);
      }
    }

    &__allMetaregions {
      height: 554px;
      border-radius: 4px 0 0 4px;
    }

    &__allCountries {
      width: 750px;
      height: 554px;
      background-color: #fff;
      position: absolute;
      ${({ $searchType }) => $searchType === SEARCH_TYPE_NAMES.PACKAGE && `left: 100%;`}
      top: 0;
      overflow: ${({ $isMobile }) => ($isMobile ? 'auto' : 'hidden')};
      padding: 10px 8px;
      border-radius: 0 4px 4px 0;

      & > :last-child {
        height: inherit;
        display: flex;
        flex-flow: column wrap;
        align-content: space-between;
      }
    }
  }

  ${({ $isMobile, $variant }) =>
    $isMobile &&
    css`
      .${destinationPickerName} {
        border-radius: 4px;

        &__control {
          width: 100%;

          ${mobileDefaultControlStyles};
          ${$variant === 'hotel' && mobileHotelPageControlStyles}
          padding: 0;
        }

        &__menu {
          height: 100vh;
          width: 100%;
          left: 0;

          // зажимаем высоту списка, 64px высота инпута destination в открытом
          &-list {
            max-height: none;
            height: calc(100vh - ${isIOS ? '150px' : '64px'});
          }
        }

        &__placeholder {
          font-weight: normal;
          color: #c2c5cc;
        }

        &__allCountries {
          width: initial;
          position: initial;
          // Отступ, так как из-за адресной строки на мобилке не видно последний элемент
          padding-bottom: 60px;
          height: calc(100vh - ${isIOS ? '150px' : '64px'});
          overflow: initial;

          & > :last-child {
            display: block;
          }

          .${destinationPickerName}__option:last-of-type {
            padding-bottom: 60px;
          }
        }

        &__icon-search {
          display: none;
          stroke: #c2c5cc;
        }
      }
    `};

  ${({ $isWizard, $menuIsOpen }) =>
    $isWizard &&
    css`
      max-height: none;
      & + ${ArrowLeftIcon} {
        stroke: #007aff;
      }

      .${destinationPickerName} {
        &__control {
          height: ${$menuIsOpen ? `48px` : `initial`};
          padding: ${$menuIsOpen ? `initial` : `16px 16px 16px 8px`};
          border: none;

          &--is-disabled {
            background-color: #fff;
          }
        }

        &__icon-search {
          display: initial;
        }

        &__single-value--is-disabled {
          color: inherit;
        }
      }
    `}

  ${({ $withBackButton }) =>
    $withBackButton &&
    css`
      position: fixed;
      top: 0;
      z-index: 1;
      padding: 8px 8px 8px 40px;
      background-color: #f5f4f2;

      .${destinationPickerName} {
        &__control {
          border: none;
        }
      }
    `};

  ${({ $isWizardOpen, $withBackButton, $image, $hasVisaLabel }) =>
    $isWizardOpen &&
    !$withBackButton &&
    css`
      .${destinationPickerName} {
        padding: ${$hasVisaLabel ? '8px 0 18px 0' : '12px 0 24px 0'};
        background: ${$image ? `url(${$image})` : `url(${DEFAULT_IMAGE}) center 45%`};
        background-size: cover;
        border-radius: 0;

        &:after {
          content: '';
          position: absolute;
          padding: 5px;
          background-color: #fff;
          bottom: 0;
          left: 0;
          right: 0;
          border-radius: 8px 8px 0 0;
        }

        &__control {
          width: fit-content;
          height: auto;
          min-width: 0;
          border-radius: 30px;
          margin: auto;
          border: none;
          font-weight: normal;
          padding: 4px 16px 8px;
          display: flex;
          flex-direction: column;

          &-visa {
            display: inline-block;
            font-size: 12px;
            line-height: 16px;
            color: #919499;
          }
        }

        &__single-value,
        &__value-container,
        &__input-container {
          display: flex;
          margin: 0;
          padding: 0;
        }

        &__icon-search {
          display: none;
        }
      }
    `};

  ${({ $withCloseButton }) =>
    $withCloseButton &&
    css`
      position: fixed;
      // Костыль для position fixed на айфон
      transform: translate3d(0, 0, 0);
      top: 0;
      left: 0;
      right: 0;
      z-index: 1;
      padding: 8px 40px 8px 8px;
      background-color: #f5f4f2;

      .${destinationPickerName} {
        &__control {
          border: none;
        }

        &__icon-search {
          display: inline;
          margin-left: 8px;
        }

        &__value-container {
          display: flex;
        }

        &__menu {
          background-color: #f5f4f2;
          border-radius: 0;
          max-height: none;

          &-list {
            max-height: none;
            background-color: #fff;
            border-radius: 8px 8px 0 0;
            height: calc(100vh - ${isIOS ? '150px' : '64px'});
          }
        }
      }
    `};

  ${({ $isMobile, $isWizard, $isWidget }) =>
    $isWidget && $isMobile && !$isWizard && mobileWidgetInputStyles}
`;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const StyledCloseButton = styled(CloseButton).attrs({ $inMobileInput: true })`
  position: fixed;
  margin: 0px;
  right: 12px;
  top: 24px;
`;

const StyledMobileLabel = styled(MobileLabel)`
  margin-bottom: 4px;
`;
